'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/routing'

const CONSENT_DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
} as const

function updateConsent(granted: boolean) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  // Advertising consent is managed separately by a Google-certified CMP.
  window.gtag('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied' })
  if (!granted) window.gtag('consent', 'update', CONSENT_DENIED)
  if (granted) {
    // Retry consent-gated tags after the visitor accepts on this page. The
    // initial GTM event may already have run while analytics was denied.
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: 'analytics_consent_granted' })
  }
}

export function CookieConsent() {
  const t = useTranslations('CookieConsent')
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    try {
      const consent = localStorage.getItem('cookie-consent')
      if (consent === 'all' || consent === 'essential') {
        updateConsent(consent === 'all')
        return
      }
    } catch {
      /* Consent remains denied when storage is unavailable. */
    }

    setVisible(true)
  }, [])

  function handleAccept() {
    try {
      localStorage.setItem('cookie-consent', 'all')
    } catch {
      /* Optional storage. */
    }
    updateConsent(true)
    setVisible(false)
  }

  function handleDecline() {
    try {
      localStorage.setItem('cookie-consent', 'essential')
    } catch {
      /* Optional storage. */
    }
    updateConsent(false)
    setVisible(false)
  }

  if (!visible)
    return (
      <button
        type="button"
        onClick={() => setVisible(true)}
        className="fixed bottom-3 left-3 z-40 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-sm"
      >
        {t('settings')}
      </button>
    )

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
      <p className="mb-4 text-sm leading-relaxed text-slate-600">
        {t('message')}{' '}
        <Link href="/cookies" className="text-primary hover:text-primary/80 font-medium underline">
          {t('learnMore')}
        </Link>
      </p>
      <div className="flex gap-3">
        <button
          onClick={handleDecline}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          {t('decline')}
        </button>
        <button
          onClick={handleAccept}
          className="bg-primary hover:bg-primary/90 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
        >
          {t('accept')}
        </button>
      </div>
    </div>
  )
}
